import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Save, Trash2, Download, Cpu, Layers } from "lucide-react";
import { toast } from "sonner";
import { calculate, fmtPerPulse, fmtCurrency } from "@/lib/calc";

const DeviceChip = ({ device, active, onClick, onDelete, currency }) => {
  const r = calculate(device.params);
  return (
    <button
      onClick={onClick}
      data-testid={`device-chip-${device.id}`}
      className={`group text-left rounded-xl border p-3 transition-all w-full ${
        active ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-border hover:border-primary/50 bg-card"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{device.name}</div>
          <div className="text-xs text-muted-foreground truncate">{device.brand || "Custom"}</div>
        </div>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onDelete(device); }}
          data-testid={`device-delete-${device.id}`}
          className="opacity-60 sm:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className="mt-2 font-heading font-bold text-primary tabular-nums">
        {fmtPerPulse(r.totalPerPulse, currency)}
        <span className="text-[10px] text-muted-foreground font-body font-normal ml-1">/shot</span>
      </div>
    </button>
  );
};

export const Devices = ({ params, currency, saved, onLoad, onSave, onDelete, activeId }) => {
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [open, setOpen] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Enter a device name"); return; }
    try {
      await onSave({ name: name.trim(), brand: brand.trim(), params });
      setName(""); setBrand(""); setOpen(false);
      toast.success("Device profile saved");
    } catch (e) {
      toast.error("Couldn't save device");
    }
  };

  const compareList = saved;

  return (
    <div className="space-y-4">
      <Card className="rounded-2xl border bg-card p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Cpu className="h-4 w-4 text-primary" />
            <h3 className="font-heading font-semibold text-base">My Lasers</h3>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="h-8 rounded-lg" data-testid="save-device-button">
                <Save className="h-3.5 w-3.5 mr-1.5" /> Save as laser
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Save this laser</DialogTitle>
                <DialogDescription>Stores the whole calculation for this laser so you can load or compare it later. Save as many lasers as you like.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1.5">
                  <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Laser name</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. My Polylase (room 2)" data-testid="save-device-name" className="h-10 rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Brand (optional)</label>
                  <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. Polylase" data-testid="save-device-brand" className="h-10 rounded-lg" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleSave} data-testid="save-device-confirm" className="rounded-lg">Save laser</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {compareList.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="devices-empty">No lasers saved yet. Enter your laser's specs on the left, then <strong className="text-foreground">Save as laser</strong> to store its calculation. Add as many as you run.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2.5">
            {compareList.map((d) => (
              <DeviceChip key={d.id} device={d} currency={currency} active={activeId === d.id}
                onClick={() => onLoad(d)} onDelete={onDelete} />
            ))}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-3">
          Each laser saves its own full calculation. Selecting one loads its saved specs and rates back into the calculator.
        </p>
      </Card>

      {/* Comparison table */}
      {compareList.length > 0 && (
      <Card className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="comparison-table">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="h-4 w-4 text-secondary" />
          <h3 className="font-heading font-semibold text-base">Compare Lasers</h3>
        </div>
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left font-mono-label text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="py-2 pr-3 font-medium">Laser</th>
                <th className="py-2 px-3 font-medium text-right">Per Shot</th>
                <th className="py-2 px-3 font-medium text-right">Per 1,000</th>
                <th className="py-2 pl-3 font-medium text-right">Reserve /yr</th>
              </tr>
            </thead>
            <tbody>
              {compareList.map((d) => {
                const r = calculate(d.params);
                return (
                  <tr key={d.id} className="border-b border-border/50 last:border-0" data-testid={`compare-row-${d.id}`}>
                    <td className="py-2.5 pr-3">
                      <div className="font-medium truncate max-w-[140px]">{d.name}</div>
                      <div className="text-xs text-muted-foreground">{d.brand || "Custom"}</div>
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums font-medium text-primary">{fmtPerPulse(r.totalPerPulse, currency)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{fmtCurrency(r.totalPerPulse * 1000, currency)}</td>
                    <td className="py-2.5 pl-3 text-right tabular-nums">{fmtCurrency(r.annualReserve, currency)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Button variant="outline" size="sm" className="w-full mt-4 rounded-lg" onClick={() => window.print()} data-testid="print-report-button">
          <Download className="h-3.5 w-3.5 mr-1.5" /> Print / Export report
        </Button>
      </Card>
      )}
    </div>
  );
};
